import type { HttpService, ResultList } from '@empathyco/playboard-core-domain'
import type { IndexJobAdapter } from '../adapter/interfaces'
import type { PlatformIndexJobEntity } from '../adapter/platform-index-job.adapter'
import type { IndexJobDTO } from '../models'
import type { IndexClientProvider } from '../providers/interfaces'
import type {
  IndexJobService,
  IndexJobServiceDiscardParams,
  IndexJobServiceFeedParams,
  IndexJobServiceJobParams,
  IndexJobServiceListParams,
  IndexJobServiceSubmitParams,
  IndexJobServiceUpdateConfigParams,
} from './interfaces'
import { interpolate } from '@empathyco/x-adapter'
import { inject, injectable, named, optional } from 'inversify'
import { TYPES } from '../config/inversify.types'
import { FeatureIdEnum } from '../enums'

@injectable()
export class PlatformIndexJobService implements IndexJobService {
  public constructor(
    @inject(TYPES.HttpService) protected httpService: HttpService<PlatformIndexJobEntity>,
    @inject(TYPES.IndexJobAdapter)
    protected adapter: IndexJobAdapter<PlatformIndexJobEntity | undefined>,
    @inject(TYPES.IndexUrl) protected url: string,
    @inject(TYPES.IndexClientProvider) protected clientProvider: IndexClientProvider,
    @inject(TYPES.Endpoint)
    @named(FeatureIdEnum.INDEX)
    @optional()
    protected endpoints = {
      list: '/jobs',
      submit: '/jobs/submit/{clientId}/{clientToken}/{feedId}{(/)skipValidations}',
      reindex: '/jobs/config/{clientId}/{clientToken}/{feedId}',
      reimport: '/jobs/reimport/{clientId}/{clientToken}/{jobId}',
      discard: '/jobs/{correlationId}',
      downloadFeed: '/data-feeds/{clientId}/{clientToken}/job/{jobId}',
      updateConfig: '/jobs',
    },
  ) {}

  async list(params: IndexJobServiceListParams): Promise<ResultList<IndexJobDTO>> {
    try {
      const response = await this.httpService.get<{
        numFound: number
        jobs: Array<PlatformIndexJobEntity>
      }>(this.buildRequestUrl(this.endpoints.list), {
        addAccessToken: true,
        parameters: {
          ...params.page,
          ...(params.filters && {
            ...(params.filters.clientIds?.length && {
              clientId: params.filters.clientIds?.join(','),
            }),
            ...(params.filters.feedIds?.length && { feedId: params.filters.feedIds?.join(',') }),
            ...(params.filters.states?.length && { state: params.filters.states?.join(',') }),
          }),
        },
      })
      return {
        results: response.data?.jobs.map(job => this.adapter.decode(job)) ?? [],
        numFound: response.data?.numFound ?? 0,
      }
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async submit(params: IndexJobServiceSubmitParams): Promise<IndexJobDTO> {
    try {
      const { token: clientToken } = await this.clientProvider(params.clientId)
      const response = await this.httpService.post(
        this.buildRequestUrl(this.endpoints.submit, {
          ...params,
          clientToken,
        }),
        {
          addAccessToken: true,
          body: params.file,
        },
      )
      return this.adapter.decode(response.data)
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async reindex(params: IndexJobServiceFeedParams): Promise<IndexJobDTO> {
    try {
      const { token: clientToken } = await this.clientProvider(params.clientId)
      const response = await this.httpService.post(
        this.buildRequestUrl(this.endpoints.reindex, {
          ...params,
          clientToken,
        }),
        {
          addAccessToken: true,
          body: params.file,
        },
      )
      return this.adapter.decode(response.data)
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async reimport(params: IndexJobServiceJobParams): Promise<IndexJobDTO> {
    try {
      const { token: clientToken } = await this.clientProvider(params.clientId)
      const response = await this.httpService.post(
        this.buildRequestUrl(this.endpoints.reimport, {
          ...params,
          clientToken,
        }),
        {
          addAccessToken: true,
        },
      )
      return this.adapter.decode(response.data)
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async discard(params: IndexJobServiceDiscardParams): Promise<IndexJobDTO> {
    try {
      const response = await this.httpService.delete<PlatformIndexJobEntity>(
        this.buildRequestUrl(this.endpoints.discard, {
          correlationId: params.correlationId,
        }),
        {
          addAccessToken: true,
        },
      )
      return this.adapter.decode(response.data)
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async downloadFeed(params: IndexJobServiceJobParams): Promise<string> {
    try {
      const { token: clientToken } = await this.clientProvider(params.clientId)
      const response = await this.httpService.get<{ feedUri: string; expirationDate: string }>(
        this.buildRequestUrl(this.endpoints.downloadFeed, {
          ...params,
          clientToken,
        }),
        {
          addAccessToken: true,
        },
      )
      return response.data?.feedUri ?? ''
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async updateConfig(params: IndexJobServiceUpdateConfigParams): Promise<IndexJobDTO> {
    try {
      const formData = new FormData()
      formData.append('clientId', String(params.clientId))
      formData.append('feedId', String(params.feedId))
      formData.append('operationType', 'CONFIG')
      formData.append('pivots', params.pivots ? JSON.stringify(params.pivots) : '{}')

      const response = await this.httpService.post(
        this.buildRequestUrl(this.endpoints.updateConfig),
        {
          addAccessToken: true,
          body: formData,
        },
      )
      return this.adapter.decode(response.data)
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  private buildRequestUrl(endpoint: string, parameters: Record<string, any> = {}): string {
    const url = `${this.url}${endpoint}`
    return interpolate(url, { ...parameters })
  }
}
